/**
 * Supplier portal API (P0-N5).
 *
 * The portal reuses party-scoped backend endpoints — the backend filters the
 * batch list / detail / exceptions to the caller's company linkage, so this
 * module adds no client-side filtering (and must not: backend is the
 * authority).
 *
 *   GET  /monthly-batches                              — own batches only
 *   GET  /monthly-batches/:id                          — own batch detail
 *   GET  /monthly-batches/:id/supplier-quotation       — latest PUBLISHED
 *        quotation (whitelist DTO: amount + coarse basis label, FR-006)
 *   GET  /monthly-batches/:id/exceptions/supplier-view — own-batch differences
 *        (whitelist projection, P0-CONF-EXC-DTO)
 *   POST /exceptions/:id/supplier-reply                — FR-007 tri-state
 *   GET  /monthly-batches/:id/external-monthly-report  — via features/reports
 */
import { apiClient } from '@/lib/api-client';
import type { ExceptionReportDto } from '@/features/exceptions/types';

export interface SupplierBatchDto {
  monthly_batch_id: string;
  facility_name: string;
  company_display_name: string;
  year_month: string;
  status: string;
  case_code: string | null;
  close_deadline: string | null;
  closed_at: string | null;
  created_at: string;
}

/** FR-006 supplier whitelist DTO — amount + coarse basis label, nothing else. */
export interface SupplierFacingQuotationDto {
  monthly_batch_id: string;
  version: number;
  amount: string;
  basis_label: 'market_middle' | 'ef_estimate' | 'dk_estimate' | 'custom';
  notes: string | null;
  published_at: string | null;
}

export type SupplierReplyValue =
  | 'return_requested'
  | 'onsite_processing_authorized'
  | 'more_info_requested';

export const fetchOwnBatches = async (): Promise<SupplierBatchDto[]> => {
  const resp = await apiClient.get<{ data: SupplierBatchDto[] }>('/monthly-batches');
  return resp.data.data;
};

export const fetchBatch = async (batchId: string): Promise<SupplierBatchDto> => {
  const resp = await apiClient.get<{ data: SupplierBatchDto }>(`/monthly-batches/${batchId}`);
  return resp.data.data;
};

export const fetchSupplierQuotation = async (
  batchId: string,
): Promise<SupplierFacingQuotationDto> => {
  const resp = await apiClient.get<{ data: SupplierFacingQuotationDto }>(
    `/monthly-batches/${batchId}/supplier-quotation`,
  );
  return resp.data.data;
};

export const fetchBatchExceptions = async (batchId: string): Promise<ExceptionReportDto[]> => {
  // The supplier-view projection (P0-CONF-EXC-DTO): no detected_payload, no
  // internal target linkage, no resolution internals.
  const resp = await apiClient.get<{ data: ExceptionReportDto[] }>(
    `/monthly-batches/${batchId}/exceptions/supplier-view`,
  );
  return resp.data.data;
};

export const postSupplierReply = async (
  exceptionId: string,
  supplierReply: SupplierReplyValue,
  note: string | undefined,
): Promise<ExceptionReportDto> => {
  const resp = await apiClient.post<{ data: ExceptionReportDto }>(
    `/exceptions/${exceptionId}/supplier-reply`,
    { supplier_reply: supplierReply, note: note || null },
  );
  return resp.data.data;
};
